<script>
import { mapGetters } from 'vuex';
import AntInput from '@/components/AntInput.vue';
import { connectionTypes } from '@/modules/ReusePotentialIndex/queries';
import { lifespanOptions } from '@/modules/ReusePotentialIndex/lifespan';
import { getColumns } from '@/services/api/column.api';
import {
  createRecord,
  deleteRecord,
  deleteRecords,
  getRecordDocument,
  updateRecord,
} from '@/services/api/record.api';
import { queryTablesV2 } from '@/services/api/v2/connect_v2';
import {
  normalizeOption,
  OPTION_FIELDS,
} from '@/modules/ReusePotentialIndex/options';

export default {
  name: 'ReusePotentialIndexCalculation',
  components: { AntInput },
  data: () => {
    return {
      newComponentMenu: false,
      newComponentName: '',
      components: [],
      componentsColumns: [],
      componentsTableId: '',
      componentsLoading: false,
      componentsListIndex: null,
      focusedComponent: null,
      componentConstructionDeviationDateMenu: false,

      connections: [],
      connectionsTableId: null,
      connectionsLoading: false,
      newConnectionMenu: false,
      newConnectionComponent: null,
      newConnectionType: null,

      focusedCrossSection: null,

      editMode: false,
    };
  },
  computed: {
    ...mapGetters(['project']),
    editable() {
      if (this.focusedComponent.status === 'created') {
        return true;
      }
      return this.focusedComponent.status === 'completed' && this.editMode;
    },
    componentLifespan() {
      return this.lifespanOptions().find(
        (x) => x.id === this.focusedComponent.technical_lifespan
      )?.label;
    },
  },
  watch: {
    focusedComponent(newValue, oldValue) {
      if (newValue) {
        if (newValue.id !== this.$route.params?.component) {
          this.$router.push({ params: { component: newValue.id } });
        }
        this.fetchComponentConnections();
        this.componentsListIndex = this.components.findIndex(
          (c) => c.id === this.$route.params.component
        );
      }
    },
  },
  async mounted() {
    await this.fetchComponents();
    if (this.$route.params.component) {
      this.focusedComponent = this.components.find(
        (c) => c.id === this.$route.params.component
      );
      this.componentsListIndex = this.components.findIndex(
        (c) => c.id === this.$route.params.component
      );
    }
  },
  methods: {
    lifespanOptions() {
      return lifespanOptions;
    },
    connectionTypes() {
      return connectionTypes;
    },
    normalizeComponentOptions(component) {
      // Convert legacy Dutch option values to English in place so the dropdowns
      // (whose items are English) display correctly and the next save persists
      // English.
      OPTION_FIELDS.forEach((field) => {
        if (component[field] != null) {
          component[field] = normalizeOption(component[field]);
        }
      });
      return component;
    },
    optionItems(columnName) {
      const column = this.componentsColumns.find((c) => c.name === columnName);
      if (!column || !Array.isArray(column.options_value)) {
        return [];
      }
      return column.options_value.map((value) => normalizeOption(value));
    },
    async fetchComponents() {
      this.componentsLoading = true;
      try {
        let { components, crossSections } = await queryTablesV2({
          tables: [
            {
              name: 'CFFA_RPI_CROSS_SECTIONS',
              project: this.project.id,
              as: 'crossSections',
              sortBy: 'title',
              records: [this.$route.params.crossSection],
              columns: [
                {
                  name: 'variant',
                  conditions: [
                    {
                      operator: '=',
                      value: this.$route.params.variant,
                    },
                  ],
                },
                {
                  name: 'title',
                },
                {
                  name: 'image',
                },
                {
                  name: 'coverage_ratio',
                },
                {
                  name: 'score',
                },
                {
                  name: 'score_revision_time',
                },
              ],
            },
            {
              name: 'CFFA_RPI_COMPONENTS',
              project: this.project.id,
              as: 'components',
              sortBy: 'nr',
              columns: [
                {
                  name: 'cross_section',
                  conditions: [
                    {
                      operator: '=',
                      value: this.$route.params.crossSection,
                    },
                  ],
                },
                {
                  name: 'nr',
                },
                {
                  name: 'name',
                },
                {
                  name: 'description',
                },
                {
                  name: 'status',
                },
                {
                  name: 'product_lvl',
                },
                {
                  name: 'component_function',
                },
                {
                  name: 'component_additional_function',
                },
                {
                  name: 'building_function',
                },
                {
                  name: 'building_additional_function',
                },
                {
                  name: 'generic_component',
                },
                {
                  name: 'construction_date_deviation',
                },
                {
                  name: 'technical_lifespan',
                },
              ],
            },
          ],
        });
        this.components = components.records;
        this.components.forEach((c) => {
          this.normalizeComponentOptions(c);
          c.progress = this.getComponentProgress(c);
        });
        this.componentsTableId = components.id;
        const columns = await getColumns(this.project.id, components.id);

        this.componentsColumns = columns.map((c) => ({
          ...c,
          options_value: JSON.parse(c.options_value),
        }));

        this.focusedCrossSection = crossSections.records[0] ?? null;
        if (this.focusedCrossSection.image?.id) {
          this.focusedCrossSection.image = await getRecordDocument(
            this.focusedCrossSection.image.id,
            this.project.id,
            crossSections.id
          );
        }
      } catch (e) {}
      this.componentsLoading = false;
    },
    async saveComponent() {
      this.componentsLoading = true;
      try {
        let component = await createRecord({
          project: { id: this.project.id },
          table: {
            id: this.componentsTableId,
          },
          record: {
            cross_section: this.$route.params.crossSection,
            name: this.newComponentName,
            nr:
              this.components.length > 0
                ? ([...this.components].sort((a, b) => b.nr - a.nr)[0].nr + 1 ??
                  1)
                : 0,
            status: 'created',
          },
        });
        this.components.push(component);
        this.focusedComponent = component;
        this.componentsListIndex = this.components.findIndex(
          (c) => c.id === this.focusedComponent.id
        );
        this.newComponentMenu = false;
        this.newComponentName = '';
      } catch (e) {}
      this.componentsLoading = false;
    },
    completeComponent() {
      this.focusedComponent.status = 'completed';
      this.updateComponent();
    },
    async updateComponent() {
      this.componentsLoading = true;
      try {
        let component = await updateRecord(this.focusedComponent.id, {
          project: { id: this.project.id },
          table: { id: this.componentsTableId },
          record: this.focusedComponent,
        });
        component.progress = this.getComponentProgress(component);
        Object.assign(
          this.components.find((c) => c.id === this.focusedComponent.id),
          component
        );
        this.focusedComponent = { ...component };
      } catch (e) {}
      this.componentsLoading = false;
    },
    async fetchComponentConnections() {
      this.connectionsLoading = true;
      try {
        let { connections } = await queryTablesV2({
          tables: [
            {
              name: 'CFFA_RPI_COMPONENTS_CONNECTIONS',
              project: this.project.id,
              as: 'connections',
              columns: [
                {
                  name: 'to_component',
                  conditions: [
                    {
                      operator: '=',
                      value: this.$route.params.component,
                    },
                  ],
                },
                {
                  name: 'from_component',
                  conditions: this.components.map((c) => {
                    return {
                      operator: '=',
                      value: c.id,
                    };
                  }),
                },
                {
                  name: 'type',
                },
              ],
            },
          ],
        });
        this.connectionsTableId = connections.id;

        this.connections = connections.records
          .map((c) => ({
            ...c,
            from_component: this.components.find(
              (x) => x.id === c.from_component
            ),
            type: connectionTypes.find((t) => t.type === c.type),
          }))
          .filter((c) => c.from_component != null);
        this.connections.forEach((c) => {
          c.from_component.progress = this.getComponentProgress(
            c.from_component
          );
        });
      } catch (e) {}
      this.connectionsLoading = false;
    },
    getComponentProgress(component) {
      const propertiesToCheck = [
        'product_lvl',
        'component_function',
        'component_additional_function',
        'technical_lifespan',
      ];
      let filledProps = 0;

      for (let i = 0; i < propertiesToCheck.length; i++) {
        let key = propertiesToCheck[i];
        if (component[key] !== null) {
          filledProps++;
        }
      }
      // calculating percentage
      return (filledProps / propertiesToCheck.length) * 100;
    },
    async saveConnection() {
      this.connectionsLoading = true;
      try {
        let connection = await createRecord({
          project: { id: this.project.id },
          table: { id: this.connectionsTableId },
          record: {
            to_component: this.focusedComponent.id,
            from_component: this.newConnectionComponent.id,
            type: this.newConnectionType.type,
          },
        });
        connection.from_component = this.components.find(
          (x) => x.id === connection.from_component
        );
        connection.from_component.progress = this.getComponentProgress(
          connection.from_component
        );

        connection.type = connectionTypes.find(
          (t) => t.type === connection.type
        );
        this.connections.push(connection);
        this.newConnectionMenu = false;
        this.newConnectionType = '';
        this.newConnectionComponent = '';
      } catch (e) {}
      this.connectionsLoading = false;
    },
    async deleteConnection(connectionId) {
      this.connectionsLoading = true;
      try {
        await deleteRecord(connectionId, {
          table: { id: this.connectionsTableId },
          project: { id: this.project.id },
        });
        this.connections = this.connections.filter(
          (c) => c.id !== connectionId
        );
      } catch (e) {}
      this.connectionsLoading = false;
    },

    async deleteComponent() {
      this.componentsLoading = true;
      try {
        let { connections } = await queryTablesV2({
          tables: [
            {
              name: 'CFFA_RPI_COMPONENTS_CONNECTIONS',
              project: this.project.id,
              as: 'connections',
              columns: [
                {
                  name: 'to_component',
                },
                {
                  name: 'from_component',
                  conditions: [
                    {
                      operator: '=',
                      value: this.$route.params.component,
                    },
                  ],
                },
                {
                  name: 'type',
                },
              ],
            },
          ],
        });

        const records = [
          ...new Set([
            ...this.connections.map((c) => c.id),
            ...connections.records.map((c) => c.id),
          ]),
        ];
        if (records.length > 0) {
          await deleteRecords({
            project: {
              id: this.project.id,
            },
            table: {
              id: this.connectionsTableId,
            },
            records: records,
          });
        }

        await deleteRecord(this.focusedComponent.id, {
          project: {
            id: this.project.id,
          },
          table: {
            id: this.componentsTableId,
          },
        });

        this.components = this.components.filter(
          (c) => c.id !== this.focusedComponent.id
        );
        this.focusedComponent = null;
        this.componentsListIndex = null;
      } catch (e) {
        console.log(e);
      }
      this.componentsLoading = false;
    },
  },
};
</script>

<template>
  <div class="px-5">
    <div class="background-white d-flex" style="width: 250px">
      <v-list class="d-flex flex-column full-width" subheader two-line>
        <div class="subheader">
          1. Parts
          <v-spacer />
          <v-menu
            v-model="newComponentMenu"
            :close-on-content-click="false"
            right
          >
            <template #activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>

            <div class="background-white pa-1">
              <v-text-field
                v-model="newComponentName"
                autofocus
                class="mb-2"
                filled
                hide-details
                placeholder="Name"
                single-line
                @keydown.enter="saveComponent"
              />
            </div>
          </v-menu>
        </div>
        <v-progress-linear
          v-show="componentsLoading"
          color="primary"
          height="5"
          indeterminate
        />
        <v-list-item-group
          v-model="componentsListIndex"
          class="flex-grow-1 overflow-y-auto px-2"
          color="primary"
        >
          <v-list-item
            v-for="component in components"
            :key="component.id"
            :style="{
              'border-left':
                component.progress > 0 && component.status !== 'completed'
                  ? 'solid 5px orange'
                  : component.status === 'created'
                    ? 'solid 5px red'
                    : 'solid 5px green',
            }"
            class="mb-2"
            @click="focusedComponent = { ...component }"
          >
            <span class="fs-16 mr-2">{{ component.nr }}.</span>
            <v-list-item-content>
              <v-list-item-title>{{ component.name }}</v-list-item-title>
              <v-list-item-subtitle
                >{{ component.description }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action
              v-if="component.progress > 0 && component.status !== 'completed'"
            >
              <v-progress-circular
                :value="component.progress"
                color="primary"
              />
            </v-list-item-action>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </div>
    <div class="d-flex flex-column pl-5 flex-grow-1">
      <div
        class="background-white full-width d-flex mb-5"
        style="min-height: 300px"
      >
        <div class="d-flex flex-column flex-grow-1 pa-5">
          <div class="subheader">{{ focusedCrossSection?.title }}</div>
        </div>

        <v-dialog style="height: 90vh" width="90vw">
          <template #activator="{ on, attrs }">
            <v-img
              :src="`data:${focusedCrossSection?.image?.mimetype};base64, ${focusedCrossSection?.image?.file}`"
              :style="{
                background: focusedCrossSection?.image ? '' : 'lightgrey',
              }"
              class="my-5 mr-5 radius-8 c-pointer"
              contain
              max-height="300"
              max-width="500"
              style="position: relative"
              v-bind="attrs"
              v-on="on"
            />
          </template>

          <v-img
            :src="`data:${focusedCrossSection?.image?.mimetype};base64, ${focusedCrossSection?.image?.file}`"
            class="radius-8"
            contain
          />
        </v-dialog>
      </div>
      <div
        v-if="focusedComponent"
        class="d-flex flex-grow-1 background-white d-flex pa-5"
        style="height: 0"
      >
        <div class="overflow-y-auto" style="max-width: 70%">
          <v-list subheader>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="subheader-small"
                  >1. Naming
                  <v-spacer />

                  <v-btn
                    v-if="focusedComponent.status !== 'completed'"
                    :disabled="focusedComponent.progress !== 100"
                    color="primary"
                    small
                    @click="completeComponent"
                  >
                    <v-progress-circular
                      v-if="focusedComponent.progress !== 100"
                      v-model="focusedComponent.progress"
                      class="mr-2"
                      color="primary"
                      size="20"
                    />
                    Complete
                  </v-btn>
                  <v-btn
                    v-if="focusedComponent.status === 'completed'"
                    :color="editMode ? 'primary' : ''"
                    icon
                    @click="editMode = !editMode"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-menu :close-on-content-click="false" right>
                    <template #activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>

                    <div class="background-white pa-1">
                      <v-list-item style="max-width: 400px">
                        <v-list-item-content>
                          <v-list-item-title
                            >Are you sure you want to delete this part?
                          </v-list-item-title>
                          <v-list-item-subtitle
                            >All connections will be lost
                          </v-list-item-subtitle>
                          <v-btn
                            color="primary"
                            elevation="0"
                            small
                            @click="deleteComponent"
                            >Delete
                          </v-btn>
                        </v-list-item-content>
                      </v-list-item>
                    </div>
                  </v-menu>
                </v-list-item-title>

                <v-list-item-subtitle
                  >Provide additional information about the part
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <div class="px-5 d-flex mb-5">
              <ant-input class="mr-5" label="Name" top-margin="mt-0">
                <template #input-field>
                  <v-text-field
                    v-model="focusedComponent.name"
                    :readonly="!editable"
                    dense
                    filled
                    hide-details
                    single-line
                    @change="updateComponent"
                  />
                </template>
              </ant-input>
              <ant-input is-optional label="Description" top-margin="mt-0">
                <template #input-field>
                  <v-text-field
                    v-model="focusedComponent.description"
                    :readonly="!editable"
                    dense
                    filled
                    hide-details
                    single-line
                    @change="updateComponent"
                  />
                </template>
              </ant-input>
            </div>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="subheader-small"
                  >2. Product level
                  <v-menu offset-x open-on-hover>
                    <template #activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon>mdi-information-box-outline</v-icon>
                      </v-btn>
                    </template>

                    <v-card max-width="600">
                      <v-card-text>
                        <p>
                          This indicator analyses the assembly process on the
                          construction site. It is important to distinguish
                          between processing materials directly on the
                          construction site, assembly of individual elements on
                          the construction site, and assembly of prefab
                          components/systems on the construction site.
                          <br />
                          Less work on the construction site during
                          assembly/disassembly results in less damage to
                          building components during the assembly/disassembly
                          process and a faster assembly/disassembly process.
                          <br /><br />Overview of the different product levels
                          (see illustration below)
                        </p>
                        <v-img
                          contain
                          max-height="300"
                          max-width="500"
                          src="./assets/assembly_lvl.png"
                        />
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </v-list-item-title>
                <v-list-item-subtitle
                  >Indicate whether the part is loose material, a
                  single-piece elmement, or part of an assembled
                  system/component.
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <div class="px-5 d-flex mb-5">
              <ant-input label="Product level" top-margin="mt-0">
                <template #input-field>
                  <v-select
                    v-model="focusedComponent.product_lvl"
                    :items="optionItems('product_lvl')"
                    :readonly="!editable"
                    dense
                    filled
                    hide-details
                    single-line
                    @change="updateComponent"
                  />
                </template>
              </ant-input>
            </div>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="subheader-small"
                  >3. Part function
                  <v-menu offset-x open-on-hover>
                    <template #activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon>mdi-information-box-outline</v-icon>
                      </v-btn>
                    </template>

                    <v-card max-width="600">
                      <v-card-text>
                        <p>
                          The fewer relations a part has, the easier it is to
                          recover the part. A part with a base/load-bearing or
                          intermediary function ensures that parts are not
                          directly connected to each other but via an
                          intermediary connector, allowing them to be
                          disassembled faster without touching each other.
                        </p>
                        <v-img
                          contain
                          max-height="300"
                          max-width="500"
                          src="./assets/component_function.png"
                        />
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </v-list-item-title>
                <v-list-item-subtitle style="white-space: normal"
                  >Indicate whether or not the part has a base/load-bearing or
                  intermediary function within a building section. And verify
                  whether there are other functions besides finishing
                  (servicing/installations, non-load-bearing partitioning).
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <div class="px-5 d-flex mb-5">
              <ant-input
                class="mr-5"
                label="Part function"
                top-margin="mt-0"
              >
                <template #input-field>
                  <v-select
                    v-model="focusedComponent.component_function"
                    :items="optionItems('component_function')"
                    :readonly="!editable"
                    dense
                    filled
                    hide-details
                    single-line
                    @change="updateComponent"
                  />
                </template>
              </ant-input>
              <ant-input
                label="Additional part function(s) other than finishing"
                top-margin="mt-0"
              >
                <template #input-field>
                  <v-select
                    v-model="focusedComponent.component_additional_function"
                    :items="optionItems('component_additional_function')"
                    :readonly="!editable"
                    dense
                    filled
                    hide-details
                    single-line
                    @change="updateComponent"
                  />
                </template>
              </ant-input>
            </div>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="subheader-small"
                  >4. Building function
                  <v-menu open-on-hover top>
                    <template #activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon>mdi-information-box-outline</v-icon>
                      </v-btn>
                    </template>

                    <v-card max-width="600">
                      <v-card-text>
                        <p>
                          Building function is not required for this
                          calculation.<br />
                          Building functions are:<br />
                          • load bearing function (such as foundation,
                          structural walls, columns, beams, floors),<br />
                          • enclosing function (such as facades and roofs),
                          partitioning functions (such as non-load bearing
                          partitioning walls),<br />
                          • finishing function (such as suspended ceiling,
                          floor finishing),<br />
                          • installation/servicing function (such as
                          electrical, sewerage, climate installations)<br />
                          <br />
                          Make sure part are mono-functional rather than
                          fulfilling multiple functions.
                        </p>
                        <v-img
                          contain
                          max-height="300"
                          max-width="500"
                          src="./assets/building_function.png"
                        />
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </v-list-item-title>
                <v-list-item-subtitle style="white-space: normal"
                  >Enter the building function of the part. Add whether it
                  fulfills multiple functions, and in case of a
                  component/system whether it can also be used directly for
                  other functions (generic component/system).
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <div class="px-5 d-flex mb-5">
              <ant-input
                class="mr-5"
                is-optional
                label="Building function"
                top-margin="mt-0"
              >
                <template #input-field>
                  <v-text-field
                    v-model="focusedComponent.building_function"
                    :readonly="!editable"
                    dense
                    filled
                    hide-details
                    single-line
                    @change="updateComponent"
                  />
                </template>
              </ant-input>
              <ant-input
                class="mr-5"
                is-optional
                label="Additional building functions"
                top-margin="mt-0"
              >
                <template #input-field>
                  <v-select
                    v-model="focusedComponent.building_additional_function"
                    :items="optionItems('building_additional_function')"
                    :readonly="!editable"
                    dense
                    filled
                    hide-details
                    single-line
                    @change="updateComponent"
                  />
                </template>
              </ant-input>
              <ant-input
                is-optional
                label="Generic component/system"
                top-margin="mt-0"
              >
                <template #input-field>
                  <v-select
                    v-model="focusedComponent.generic_component"
                    :items="optionItems('generic_component')"
                    :readonly="!editable"
                    dense
                    filled
                    hide-details
                    single-line
                    @change="updateComponent"
                  />
                </template>
              </ant-input>
            </div>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="subheader-small"
                  >5. Lifespan
                </v-list-item-title>
                <v-list-item-subtitle style="white-space: normal"
                  >Enter the technical lifespan of all parts (see
                  worksheet 'appendix lifecycle'), and the construction date
                  if it differs from the general information.
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <div class="px-5 d-flex mb-5">
              <ant-input
                class="mr-5"
                is-optional
                label="Construction date deviation"
                top-margin="mt-0"
              >
                <template #input-field>
                  <v-menu
                    v-model="componentConstructionDeviationDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    min-width="auto"
                    offset-y
                    transition="scale-transition"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="focusedComponent.construction_date_deviation"
                        clearable
                        dense
                        filled
                        prepend-icon="mdi-calendar"
                        readonly
                        single-line
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="focusedComponent.construction_date_deviation"
                      @change="updateComponent"
                      @input="componentConstructionDeviationDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </template>
              </ant-input>

              <ant-input
                label="Technical lifespan (years)"
                top-margin="mt-0"
              >
                <template #input-field>
                  <v-select
                    v-model="focusedComponent.technical_lifespan"
                    :items="lifespanOptions()"
                    :readonly="!editable"
                    :return-object="false"
                    dense
                    filled
                    hide-details
                    item-text="label"
                    item-value="id"
                    single-line
                    type="number"
                    @change="updateComponent"
                  >
                    <template #item="{ item }">
                      {{ item.label }} ({{ item.years }})
                    </template>
                  </v-select>
                </template>
              </ant-input>
            </div>
          </v-list>
        </div>
        <div class="flex-grow-1 overflow-y-auto">
          <v-list class="px-2" subheader two-line>
            <div class="subheader-small">
              6. Type of connections
              <v-menu open-on-hover top>
                <template #activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-information-box-outline</v-icon>
                  </v-btn>
                </template>

                <v-card max-width="600">
                  <v-card-text>
                    <p>
                      Select the correct type of connection from the drop-down menu.
                    </p>
                    <v-img
                      contain
                      max-height="300"
                      max-width="500"
                      src="./assets/connections.png"
                    />
                  </v-card-text>
                </v-card>
              </v-menu>
              <v-spacer />
              <v-menu
                v-model="newConnectionMenu"
                :close-on-content-click="false"
                right
              >
                <template #activator="{ on, attrs }">
                  <v-btn :disabled="!editable" icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>

                <v-card>
                  <v-card-text>
                    <ant-input label="Part" top-margin="mt-2">
                      <template #input-field>
                        <v-combobox
                          v-model="newConnectionComponent"
                          :items="components"
                          autofocus
                          filled
                          hide-details
                          item-text="name"
                          item-value="id"
                          placeholder="Part"
                          single-line
                        >
                          <template #item="{ item }">
                            <span class="font-weight-bold mr-2">{{
                              item.nr
                            }}</span>
                            {{ item.name }}
                          </template>
                        </v-combobox>
                      </template>
                    </ant-input>
                    <ant-input label="Type" top-margin="mt-0">
                      <template #input-field>
                        <v-combobox
                          v-model="newConnectionType"
                          :items="connectionTypes()"
                          class="mb-2"
                          filled
                          hide-details
                          item-text="type"
                          item-value="type"
                          placeholder="type"
                          single-line
                        >
                          <template #item="{ item }">
                            <span class="font-weight-bold mr-2">{{
                              item.type
                            }}</span>
                            {{ item?.label }}
                          </template>
                        </v-combobox>
                      </template>
                    </ant-input>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" small @click="saveConnection"
                      >Save
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </div>
            <v-progress-linear
              v-show="connectionsLoading"
              color="primary"
              height="5"
              indeterminate
            />
            <v-list-item-group
              class="flex-grow-1 overflow-y-auto px-2"
              color="primary"
            >
              <v-list-item
                v-for="connection in connections"
                :key="connection.id"
                :style="{
                  'border-left':
                    connection.from_component.progress > 0 &&
                    connection.from_component.progress < 100
                      ? 'solid 5px orange'
                      : connection.from_component.status === 'created'
                        ? 'solid 5px red'
                        : 'solid 5px green',
                }"
                class="mb-2"
                @click="focusedComponent = { ...connection.from_component }"
              >
                <span class="fs-16 mr-2"
                  >{{ connection.from_component.nr }}.</span
                >
                <v-list-item-content>
                  <v-list-item-title
                    >{{ connection.from_component.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    >{{ connection.type?.label }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action>
                  <div class="d-flex align-center">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-chip
                          class="mr-2"
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          >{{ connection.type?.type }}
                        </v-chip>
                      </template>
                      <span>{{ connection.type?.label }}</span>
                    </v-tooltip>
                    <v-btn icon @click="deleteConnection(connection.id)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.subheader {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  color: var(--v-primary-base);
  font-size: 20px;
}

.subheader-small {
  display: flex;
  align-items: center;
  color: var(--v-primary-base);
}
</style>
